import { GovIcon } from '@gov-design-system-ce/react';
import type { ContactDataSection, PhoneFormData } from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { PageCode } from '@gov-nx/module/page';
import { ConfirmationPrompt, InfoTooltip } from '@gov-nx/ui/web';
import { formatPhoneNumber } from '@gov-nx/utils/common';
import { ActionButtons } from './ActionButtons';
import { ContactPhoneForm } from './ContactPhoneForm';
import { ContactDetailSkeleton } from './Skeletons';

interface ContactPhoneViewProps extends ContactDataSection<PhoneFormData> {
	code: PageCode;
	externalEditUrl?: string;
}

export const ContactPhoneView = ({
	code,
	tooltipText,
	isReloading,
	isDeleteConfirmationOpen,
	valueVerified,
	formDefinition,
	onDelete,
	onResendVerification,
	onSubmit,
	onChange,
	isEditing,
	isChanging,
	isUpdating,
	isNeedForVerification,
	isLowAuthorization,
	isAuthorizationEnforced,
	isDeleting,
	isVerificationResending,
	isFormOpen,
	externalEditUrl,
}: ContactPhoneViewProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(PageCode['nastaveni']);

	if (isReloading) {
		return <ContactDetailSkeleton />;
	}

	return (
		<div>
			<dl className="flex flex-col gap-(--spacing-s) p-(--templates-margin-l) text-(--text-secondary)">
				<dt className="flex justify-between gap-(--spacing-xs)">
					<div className="flex items-center">
						<h3 className="text-body-l-bold! grow">
							{tsn('kontakt.telefonni-cislo')}
						</h3>
						<InfoTooltip content={tooltipText} />
					</div>

					<ActionButtons
						isEditing={isEditing}
						externalEditUrl={externalEditUrl}
						isChanging={isChanging}
						isConfirmationOpen={isDeleteConfirmationOpen}
						isNeedForVerification={isNeedForVerification}
						value={valueVerified}
						strings={{
							buttons: {
								cancel: {
									label: tsn('kontakt.akce.zrusit'),
									wcagLabel: tsn('kontakt.akce.wcag.zrusit-telefon'),
								},
								edit: {
									label: tsn('kontakt.akce.upravit'),
									wcagLabel: tsn('kontakt.akce.wcag.upravit-telefon'),
								},
								delete: {
									wcagLabel: tsn('kontakt.akce.wcag.smazat-telefon'),
								},
								externalEdit: {
									label: tsn('kontakt.akce.upravit-v-po'),
									wcagLabel: tsn('kontakt.akce.wcag.upravit-v-po'),
								},
							},
						}}
					/>
				</dt>
				<dd>
					{isFormOpen && !externalEditUrl ? (
						<ContactPhoneForm
							code={code}
							onSubmit={onSubmit}
							onChange={onChange}
							isUpdating={isUpdating}
							resendVerification={onResendVerification}
							isVerificationResending={isVerificationResending}
							isNeedForVerification={isNeedForVerification}
							isLowAuthorization={isLowAuthorization}
							isAuthorizationEnforced={isAuthorizationEnforced}
							formDefinition={formDefinition}
						/>
					) : valueVerified ? (
						formatPhoneNumber(valueVerified)
					) : (
						<>{tsn('kontakt.telefon-nevyplneno')}</>
					)}
				</dd>
			</dl>
			<ConfirmationPrompt
				isOpen={isDeleteConfirmationOpen.value}
				onConfirm={onDelete}
				onCancel={isDeleteConfirmationOpen.setFalse}
				isDisabled={isDeleting}
				isLoading={isDeleting}
				icon={
					<GovIcon
						name="trash"
						slot="icon"
						type="templates"
						color="error"
					/>
				}
				strings={{
					title: tsn('kontakt.akce.smazat.potvrzeni.titulek'),
					buttons: {
						cancel: {
							label: tsn('kontakt.akce.smazat.potvrzeni.nemazat'),
							wcagLabel: tsn('kontakt.akce.smazat.potvrzeni.wcag.nemazat'),
						},
						confirm: {
							label: tsn('kontakt.akce.smazat.potvrzeni.smazat'),
							wcagLabel: tsn('kontakt.akce.smazat.potvrzeni.wcag.smazat'),
						},
					},
				}}>
				{tsn('kontakt.akce.smazat.potvrzeni.otazka')}
			</ConfirmationPrompt>
		</div>
	);
};
